<template>
    <div class="uik-datePicker" :class="{'uik-datePicker--active': active}">
        <p v-if="label !== ''" class="uik-datePicker__label">
            {{ label }}
            {{ (required ? '*':'') }}
        </p>
        <DatePicker
            v-if="!inline"
            :class="{ 'uik-datePicker__errorHighlight': errorMessage !== '' ? true : false }"
            :clearable="false"
            :first-day-of-week="1"
            :format="format"
            :lang="lang_settings"
            :not-before="notBefore"
            :not-after="notAfter"
            :placeholder="placeholder ? placeholder : ''"
            :range="range"
            :shortcuts="shortcuts"
            :value="date"
            :disabled="disabled"
            @change="emit('change', $event)"
        />
        <transition name="datepicker-inline-active-date">
            <div
                v-if="inline && active"
                class="uik-datePicker__inline-active-date"
            >
                <span>{{ date | moment }}</span>
                <button v-if="!disabled" class="uik-datePicker__clear" @click="$emit('change', null)">
                    <Uikon>add</Uikon>
                </button>
            </div>
        </transition>
        <DatePickerWrapper
            :language="lang_settings"
            class="mx-datepicker"
            :class="{'mx-datepicker--inline': inline}"
        >
            <CalendarPanel
                v-if="inline && !range"
                class="mx-datepicker-inline"
                visible
                :value="date"
                :placeholder="placeholder ? placeholder : ''"
                :lang="lang_settings"
                :date-format="format"
                :clearable="false"
                :range="range"
                :first-day-of-week="1"
                :shortcuts="shortcuts"
                :notBefore="notBefore"
                :notAfter="notAfter"
                :disabled="disabled"
                @select-date="$emit('change', $event)"
            />
            <CalendarPanel
                v-if="inline && range"
                class="mx-datepicker-inline mx-datepicker-inline-range"
                visible
                :value="rangeStartDate"
                :placeholder="placeholder ? placeholder : ''"
                :lang="lang_settings"
                :date-format="format"
                :clearable="false"
                :range="range"
                :first-day-of-week="1"
                :shortcuts="shortcuts"
                :end-at="Array.isArray(date) ? date[1] : null"
                :start-at="null"
                :notBefore="notBefore"
                :notAfter="notAfter"
                :disabled="disabled"
                @select-date="setStartDateRange"
            />
            <CalendarPanel
                v-if="inline && range"
                class="mx-datepicker-inline mx-datepicker-inline-range"
                visible
                :value="rangeEndDate"
                :placeholder="placeholder ? placeholder : ''"
                :lang="lang_settings"
                :date-format="format"
                :clearable="false"
                :range="range"
                :first-day-of-week="1"
                :shortcuts="shortcuts"
                :start-at="Array.isArray(date) ? date[0] : null"
                :end-at="null"
                :notBefore="notBefore"
                :notAfter="notAfter"
                :disabled="disabled"
                @select-date="setEndDateRange"
            />
        </DatePickerWrapper>
        <button v-if="active && !inline && !disabled" class="uik-datePicker__clear" @click="reset()">
            <Uikon>add</Uikon>
        </button>
        <p v-if="errorMessage" class="uik-datePicker__errorMessage">{{ errorMessage }}</p>
    </div>
</template>

<script>
import DatePicker from "vue2-datepicker2"
import moment from "moment"
const CalendarPanel = DatePicker.components.CalendarPanel
export default {
  name: "UikDatePicker",
  components: {
    DatePicker,
    CalendarPanel,
    DatePickerWrapper: {
      name: "DatePicker",
      props: {
        language: {
          type: Object
        }
      },
      render (h) {
        return h("div", this.$slots.default)
      }
    }
  },
  filters: {
    moment: (date) => {
      if (Array.isArray(date)) return moment(date[0], "YYYY.MM.DD").format("ddd, MMMM Do YYYY") + " - " + moment(date[1], "YYYY.MM.DD").format("ddd, MMMM Do YYYY")
      return moment(date, "YYYY.MM.DD").format("ddd, MMMM Do YYYY")
    }
  },
  model: {
    prop: "date",
    event: "change"
  },
  valueType: "",
  props: {
    /**
         * The selected value of the datepicker (can be a moment object)
         */
    date: { type: [String, Array, Date, Object, null], default: () => [] },
    /**
         * The error message to display
         */
    errorMessage: { type: String, default: "" },
    /**
         * The output date format
         */
    format: { type: String, default: "DD.MM.YYYY" },
    /**
         * Should the datepicker be open all the time?
         */
    inline: { type: Boolean, default: false },
    /**
         * The datepicker label
         */
    label: { type: String, default: "" },
    /**
         * The oldest date the user can pick
         */
    notBefore: { type: [Date, String], default: "" },
    /**
         * The newest date the user can pick
         */
    notAfter: { type: [Date, String], default: "" },
    /**
         * The datepicker placeholder
         */
    placeholder: { type: String, default: "" },
    /**
         * Should the user be able to select a date range
         */
    range: { type: Boolean, default: false },
    /**
         * Is this field required?
         */
    required: { type: Boolean, default: false },
    /**
         * Is this field disabled?
         */
    disabled: { type: Boolean, default: false }
  },
  data () {
    return {
      tempDateRange: [null, null],
      lang_settings: {},
      shortcuts: [
        {
          text: "Danes",
          onClick: () => {
            this.$emit("change", [this.moment()._d, this.moment()._d])
          }
        },
        {
          text: "Tekoči mesec",
          onClick: () => {
            this.$emit("change", [this.moment().startOf("month")._d, this.moment()._d])
          }
        },
        {
          text: "Tekoče leto",
          onClick: () => {
            this.$emit("change", [this.moment().startOf("year")._d, this.moment()._d])
          }
        },
        {
          text: "Pretekli mesec",
          onClick: () => {
            this.$emit("change", [this.moment().add(-1, "month").startOf("month")._d, this.moment().add(-1, "month").endOf("month")._d])
          }
        },
        {
          text: "Zadnjih 30 dni",
          onClick: () => {
            this.$emit("change", [this.moment().add(-30, "days")._d, this.moment()._d])
          }
        }
      ]
    }
  },
  computed: {
    active () {
      return this.date && (this.date.length > 0 || (!Array.isArray(this.date)))
    },
    rangeStartDate () {
      if (this.tempDateRange[0] && !this.tempDateRange[1]) return this.tempDateRange[0]
      if (Array.isArray(this.date) && this.date[0] && this.date[1]) return this.date[0]
      return this.tempDateRange[0]
    },
    rangeEndDate () {
      if (this.tempDateRange[1] && !this.tempDateRange[0]) return this.tempDateRange[1]
      if (Array.isArray(this.date) && this.date[0] && this.date[1]) return this.date[1]
      return this.tempDateRange[1]
    }
  },
  created () {
    this.setLocale()
    if (Array.isArray(this.date) && this.date.length >= 2) {
      this.tempDateRange = [new Date(this.date[0]), new Date(this.date[1])]
    }
  },
  methods: {
    emit (event, value) {
      try {
        let valueFormat = this.$root._data.uik.datepicker.valueFormat
        if (valueFormat) {
          if (this.range) {
            this.$emit(event, value.map(el => this.moment(el).format(valueFormat)))
            return
          }
          this.$emit(event, this.moment(value).format(valueFormat))
          return
        }
      } catch {}
      this.$emit(event, value)
    },
    setStartDateRange (date) {
      if (this.tempDateRange[1]) { this.$emit("change", [date, this.tempDateRange[1]]) }
      this.$emit("pick", [date, this.tempDateRange[1]])
      this.tempDateRange = [date, this.tempDateRange[1]]
    },
    setEndDateRange (date) {
      if (this.tempDateRange[0]) { this.$emit("change", [this.tempDateRange[0], date]) }
      this.$emit("pick", [this.tempDateRange[0], date])
      this.tempDateRange = [this.tempDateRange[0], date]
    },
    reset () {
      this.tempDateRange = [null, null]
      this.$emit("change", null)
    },
    setLocale () {
      let projectConfig
      try {
        projectConfig = this.$root._data.uik.datepicker
      } catch {}

      const shortcuts = projectConfig?.shortcuts
      if (shortcuts) {
        for (let i = 0; i < this.shortcuts.length; i++) {
          if (shortcuts[i]) this.shortcuts[i].text = shortcuts[i]
        }
      }

      this.lang_settings = projectConfig?.lang_settings || {
        days: ["Ned", "Pon", "Tor", "Sre", "Čet", "Pet", "Sob"],
        months: [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "Maj",
          "Jun",
          "Jul",
          "Avg",
          "Sep",
          "Okt",
          "Nov",
          "Dec"
        ],
        pickers: [
          "Naslednjih 7 dni",
          "Naslednjih 30 dni",
          "Zadnjih 7 dni",
          "Zadnjih 30 dni"
        ],
        placeholder: {
          date: "Izberi datum",
          dateRange: "Izberi časovno obdobje"
        }
      }
    }
  }
}
</script>

<style lang="scss">
.uik-datePicker {
    display: flex;
    flex-flow: column nowrap;
    justify-content: center;
    align-items: flex-start;
    position: relative;
    .uik-datePicker__label {
        white-space: nowrap;
        font-size: 12px;
        color: $textSecondary;
        font-weight: 500;
        text-transform: uppercase;
        display: inline-block;
        margin: 0 0 8px 0;
    }
    .mx-datepicker {
        margin: 0;
        width: 100%;
        display: flex;
        &.uik-datePicker__errorHighlight {
          .mx-input-wrapper {
            .mx-input {
              border-color: $error;
            }
          }
        }
        .mx-input-wrapper {
            .mx-input {
                padding: 10px 15px;
                background: transparent;
                border: $borderDefault;
                box-shadow: none;
                font-size: 14px;
                line-height: 1;
                cursor: text;
                transition: all 0.15s;
                height: unset;
                &:focus {
                  border-color: $primary;
                }
                &::placeholder {
                    color: $textSecondary;
                }
            }
            .mx-input-append {
                display: none;
            }
        }
        .mx-datepicker-popup,
        .mx-datepicker-inline {
            background: white;
            border-radius: 4px;
            border-color: $borderColorDarker;
            box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
            .mx-shortcuts-wrapper {
                display: flex;
                flex-flow: row wrap;
                justify-content: space-between;
                padding: 10px;
                .mx-shortcuts {
                    display: inline-flex;
                    align-items: center;
                    text-align: center;
                    white-space: nowrap;
                    vertical-align: middle;
                    user-select: none;
                    height: 32px;
                    padding: 0 12px;
                    font-size: 0.8rem;
                    font-weight: 400;
                    position: relative;
                    background: #ffffff;

                    border: 1px solid $borderColorDarker;
                    box-shadow: 0 1px 1px 0 rgba(22, 29, 37, 0.05),
                        inset 0 2px 0 0 rgba(255, 255, 255, 0.05);
                    color: $textMain;
                    border-radius: 4px;
                    &:hover {
                        border: 1px solid #cacedb;
                        box-shadow: 0 1px 2px 0 rgba(22, 29, 37, 0.1),
                            inset 0 2px 0 0 rgba(255, 255, 255, 0.05);
                    }
                    &:focus {
                        color: white;
                        background: $primary;
                        background: $primary;
                        background-image: linear-gradient(0deg, $primary 2%, lighten($primary, 2%) 98%);
                        border: 1px solid darken($primary, 3%);
                        box-shadow: 0 1px 1px 0 rgba(22, 29, 37, 0.1),
                            inset 0 2px 0 0 rgba(255, 255, 255, 0.06);
                    }
                    &::after {
                        content: none;
                    }
                }
            }
            .mx-calendar-header {
                .mx-icon-last-year,
                .mx-icon-last-month,
                .mx-icon-next-year,
                .mx-icon-next-month {
                    color: $textSecondary;
                    &:hover {
                        color: $primary;
                    }
                }
                .mx-current-month,
                .mx-current-year {
                    color: $textMain;
                    &:hover {
                        color: $primary;
                    }
                }
            }
            .mx-calendar-content {
                .mx-panel-year,
                .mx-panel-month {
                    .cell {
                        color: $textMain;
                        border-radius: 4px;
                        &:hover {
                            background-color: rgba($primary, 0.1);
                        }
                    }
                    .actived {
                        background-color: $primary;
                        color: white;
                        &:hover {
                            background-color: $primary;
                        }
                    }
                    .disabled {
                        background-color: rgba($textLight, 0.2);
                        color: $textLight;
                        &:hover {
                            background-color: rgba($textLight, 0.2);
                        }
                    }
                }
                .mx-panel-date {
                    thead {
                        tr {
                            th {
                                color: $textBase;
                            }
                        }
                    }
                    tbody {
                        tr {
                            .cell {
                                border-radius: 4px;
                                &:hover {
                                    background-color: rgba($primary, 0.1);
                                }
                            }
                            .last-month,
                            .next-month {
                                color: $textLight;
                            }
                            .cur-month {
                                color: $textMain;
                            }
                            .today {
                                color: $primary;
                                font-weight: bold;
                            }
                            .inrange {
                                background-color: rgba($primary, 0.1);
                                border-radius: 0;
                            }
                            .disabled {
                                background-color: rgba($textLight, 0.2);
                                border-radius: 0;
                                &:hover {
                                    background-color: rgba($textLight, 0.2);
                                }
                            }
                            .last-month.disabled:hover,
                            .next-month.disabled:hover {
                                color: $textLight;
                            }
                            .actived {
                                background-color: $primary;
                                color: white;
                                &:hover {
                                    background-color: $primary;
                                }
                            }
                        }
                    }
                }
            }
            &-range {
              box-shadow: none;
              border: $borderDefault;
              &:first-child {
                border-radius: 4px 0 0 4px;
              }
              &:last-child {
                border-left: none;
                border-radius: 0 4px 4px 0;
              }
            }
        }
    }
    .uik-datePicker__inline-active-date {
      width: 100%;
      display: flex;
      flex-flow: row nowrap;
      justify-content: flex-start;
      align-items: center;
      position: relative;
      background: $primary;
      color: white;
      padding: 8px 25px 8px 12px;
      margin-top: 0;
      border-radius: 4px 4px 0 0;
      span {
        font-size: 14px;
        width: 100%;
        display: block;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .uik-datePicker__clear {
        bottom: unset;
        right: 4px;
        .uikon {
          color: whitesmoke;
        }
        &:hover {
          .uikon {
            color: white;
          }
        }
      }
    }
    .datepicker-inline-active-date-enter-active, .datepicker-inline-active-date-leave-active {
      transition: all 0.2s !important;
      span {
        transition: all 0.1s !important;
      }
    }
    .datepicker-inline-active-date-enter, .datepicker-inline-active-date-leave-to {
      height: 0;
      padding: 0 25px 0 12px;
      span {
        opacity: 0;
      }
    }
    .uik-datePicker__clear {
        display: inline-flex;
        justify-content: center;
        align-items: center;
        position: absolute;
        width: 23px;
        height: 23px;
        margin: 0;
        padding: 5px;
        background: transparent;
        border: none;
        cursor: pointer;
        outline: none;
        right: 5px;
        bottom: 8px;
        i {
            font-size: 11px;
            position: absolute;
            transform: rotate(45deg);
            color: $textSecondary;
            transition: color 0.15s;
        }
        &:hover i {
            color: $textBase;
        }
        &:active i {
            color: $textSecondary;
        }
    }
    &--active {
      .mx-datepicker .mx-input-wrapper .mx-input {
          padding-right: 30px;
      }
  }
  .uik-datePicker__errorMessage {
    color: $error;
    line-height: 1.375rem;
    text-transform: unset;
  }
}
</style>
